import { createContext, useContext, ReactNode, useMemo, useState, useEffect, useCallback } from "react";

import { FormattedFile, useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import { EntityRecord } from "@/types/entity";
import { ISwivuProject } from "@/types/SwivuProject";
import { isImageFileTyped } from "@/react/components/entityViews/files/components/FileHelpers";
import { formatFileMetadata } from "@/react/components/entityViews/files/utils/fileMetadataUtils";
import { MetadataRow } from "@/react/components/entityViews/files/utils/fileMetadataUtils";
import { TComboOption } from "@/modern/components/ui/combobox";
import { BoardFile } from "@/bryntum-lib/models/BoardFile";
import { formatFile, getFileExtension } from "@/modern/utils";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

import { useEntityManager } from "@/react/hooks/useEntityManager";

import { useImageLoading } from "../../../../hooks/useImageLoading";
import { useFileNavigation } from "../hooks/useFileNavigation";
import { useFileSpotIncentive } from "../../../../hooks/useFileSpotIncentive";
import { useDocumentExtraction, ExtractionRequestBody } from "../components/extraction/hooks/useDocumentExtraction";
import { useDocumentTextCache } from "../components/extraction/hooks/useDocumentTextCache";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { usePdfTextSearch } from "../components/extraction/hooks/usePdfTextSearch";
import { ExtractionType, ExtractionState, EXTRACTION_OPERATIONS } from "../components/extraction/types";

interface FileDetailModalContextValue {
  // File state
  file: FormattedFile | null;
  files: FormattedFile[];
  imageFile: FormattedFile | null;
  imageUrlToLoad: string | undefined;

  // Image loading state
  imageLoading: boolean;
  showSpinner: boolean;
  displayedImageUrl: string | undefined;
  currentImageUrl: string | undefined;
  handleLoadComplete: () => void;

  // Navigation state
  hasPrevious: boolean;
  hasNext: boolean;
  handlePrevious: () => void;
  handleNext: () => void;

  // File permissions
  canEditFile: boolean;
  handleFileDownload: (file: FormattedFile) => void;
  deleteFile: (id: string | number) => void;

  // Spot incentive state
  shouldShowAdjustPoints: (file: FormattedFile | null) => boolean;
  handleAdjustPoints: (file: FormattedFile, isCreatingUnreleasedRecord?: boolean, onReviewClick?: () => void) => Promise<void>;

  // UI state
  activeTab: "review" | "summary";
  setActiveTab: (tab: "review" | "summary") => void;
  summaryTab: "details" | "findings" | "points" | "share";
  setSummaryTab: (tab: "details" | "findings" | "points" | "share") => void;
  isAnnotating: boolean;
  setIsAnnotating: (value: boolean) => void;
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  isAnalyzeDialogOpen: boolean;
  setIsAnalyzeDialogOpen: (open: boolean) => void;

  // Modal state
  open: boolean;
  setOpen: (open: boolean) => void;

  // Computed values
  metadataRows: MetadataRow[];
  shouldShowSpotIncentiveButton: boolean;

  // External dependencies
  entityRecord: EntityRecord;
  project: ISwivuProject | null;
  swivuAppGrants: TComboOption[];
  editorHandler: any;
  handleFileOpen: (file: FormattedFile) => void;

  // Handlers
  handleAnnotating: () => void;
  handleCreateSpotIncentive: () => void;
  fileChangesVersion: number;

  // Extraction panel state
  isExtractable: boolean;
  isExtractionPanelOpen: boolean;
  setIsExtractionPanelOpen: (open: boolean) => void;
  activeExtraction: ExtractionType | null;
  extractionStates: Partial<Record<ExtractionType, ExtractionState>>;
  handleRunExtraction: (type: ExtractionType) => void;
  handleRunAllExtractions: () => void;
  handleViewExtraction: (type: ExtractionType) => void;
  handleCloseExtractionPanel: () => void;
  handleBackToOperationList: () => void;

  // PDF search
  pdfSearchText: string | null;
  handleSearchInPdf: (searchText: string) => void;
  clearPdfSearch: () => void;

  // Entity link
  copyEntityLink: ReturnType<typeof useEntityManager>['copyEntityLink'];

  // Promote panel state
  isPromotePanelOpen: boolean;
  setIsPromotePanelOpen: (open: boolean) => void;
  isFilePromoted: boolean;
  handleClosePromotePanel: () => void;
  promoteAiSuggestions: string[];
  setPromoteAiSuggestions: (suggestions: string[]) => void;
  promoteVisibilityWarnings: string[];
  setPromoteVisibilityWarnings: (warnings: string[]) => void;
}

const FileDetailModalContext = createContext<FileDetailModalContextValue | undefined>(undefined);

interface FileDetailModalProviderProps {
  children: ReactNode;
  entityRecord: EntityRecord;
  file: FormattedFile | null;
  navigationFiles?: FormattedFile[];
  open: boolean;
  setOpen: (open: boolean) => void;
  project: ISwivuProject | null;
  swivuAppGrants: TComboOption[];
  editorHandler: any;
  autoOpenPromotePanel?: boolean;
}

export const FileDetailModalProvider = ({ children, entityRecord, file, navigationFiles, open, setOpen, project, swivuAppGrants, editorHandler, autoOpenPromotePanel }: FileDetailModalProviderProps) => {
  const [localFile, setLocalFile] = useState<FormattedFile | null>(file);
  const { files: contextFiles, handleFileOpen, deleteFile, handleFileDownload } = useEntityFileContext();
  const files = navigationFiles ?? contextFiles;
  const [activeTab, setActiveTab] = useState<"review" | "summary">("review");
  const [summaryTab, setSummaryTab] = useState<"details" | "findings" | "points" | "share">("details");
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAnalyzeDialogOpen, setIsAnalyzeDialogOpen] = useState(false);

  // Extraction panel state
  const [isExtractionPanelOpen, setIsExtractionPanelOpen] = useState(false);
  const [activeExtraction, setActiveExtraction] = useState<ExtractionType | null>(null);
  const [extractionStates, setExtractionStates] = useState<Partial<Record<ExtractionType, ExtractionState>>>({});
  const { runExtraction, cancelAllExtractions } = useDocumentExtraction();
  const { prefetchDocumentText, awaitDocumentText, cancelPrefetch } = useDocumentTextCache();

  // Promote panel state
  const [isPromotePanelOpen, setIsPromotePanelOpen] = useState(autoOpenPromotePanel ?? false);
  const [promoteAiSuggestions, setPromoteAiSuggestions] = useState<string[]>([]);
  const [promoteVisibilityWarnings, setPromoteVisibilityWarnings] = useState<string[]>([]);

  // PDF text search
  const { pdfSearchText, searchInPdf, clearSearch } = usePdfTextSearch();

  const handleSearchInPdf = useCallback((searchText: string) => {
    searchInPdf(searchText);
  }, [searchInPdf]);

  // Check if file is an image to determine if we should load it
  const imageFile = useMemo(() => (isImageFileTyped(localFile) ? localFile : null), [localFile]);
  const imageUrlToLoad = imageFile?.originalImageUrl;
  // Compute canEditFile directly
  const canEditFile = Boolean(localFile?.canEdit);

  const EXTRACTABLE_EXTENSIONS = ["pdf", "xlsx", "xls", "docx", "csv", "tsv", "txt"];
  const isExtractable = EXTRACTABLE_EXTENSIONS.includes(getFileExtension(localFile?.name));

  // Custom hooks for business logic
  const { imageLoading, showSpinner, displayedImageUrl, currentImageUrl, handleLoadComplete } = useImageLoading(imageFile?.id, imageUrlToLoad);
  const { hasPrevious, hasNext, handlePrevious, handleNext } = useFileNavigation(localFile, files, handleFileOpen);
  const { shouldShowAdjustPoints, handleAdjustPoints } = useFileSpotIncentive({
    entityRecord,
    project,
    editorHandler
  });

  const { subscribe: subscribeToFile, version: fileChangesVersion } = useSwivuStore<BoardFile>({ storeId: STORE_KEYS.boardFilesStore });
  const { version: marketingAssetVersion } = useSwivuStore({ storeId: STORE_KEYS.marketingAssetStore });
  const { user, entityManager } = useSwivuBryntumContext();
  const { copyEntityLink } = entityManager;

  useEffect(() => {
    return subscribeToFile(({record: changedRecord}) => {
      if (!localFile?.isCCImage && changedRecord.id === localFile?.id) {
        const [f] = formatFile([changedRecord], user);
        setLocalFile(f);
        return true;
      }
      return false;
    });
  }, [subscribeToFile, localFile?.id]);

  useEffect(() => {
    if (file) {
      setLocalFile(file);
    }
  }, [file?.id]);

  // Computed values
  const metadataRows = useMemo(() => formatFileMetadata(localFile), [localFile]);
  const shouldShowSpotIncentiveButton = shouldShowAdjustPoints(localFile);

  // Handlers
  const handleAnnotating = useCallback(() => {
    if (localFile && project && localFile.originalImageUrl) {
      setIsAnnotating(true);
    }
  }, [localFile, project, setOpen]);

  const handleCreateSpotIncentive = useCallback(() => {
    if (localFile) {
      handleAdjustPoints(localFile, true, (newFile?: BoardFile) => {
        if (localFile.isCCImage && newFile) {
          const [f] = formatFile([newFile], user);
          handleFileOpen(f);
        }
        setActiveTab("summary");
        setSummaryTab("points");
        setOpen(true);
      });
    }
  }, [localFile, handleAdjustPoints, setActiveTab, setOpen]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const isFilePromoted = useMemo(() => {
    if (!project || !localFile) return false;
    const marketingAssetStore = project.marketingAssetStore;
    if (!marketingAssetStore) return false;
    return marketingAssetStore.query(() => true, true).some((record: any) => record.file === localFile.id);
  }, [project, localFile, marketingAssetVersion]);

  const handleClosePromotePanel = useCallback(() => {
    setIsPromotePanelOpen(false);
    setPromoteAiSuggestions([]);
    setPromoteVisibilityWarnings([]);
  }, []);

  // Builds the shared extraction request body for all document-ai endpoints
  const buildExtractionBody = useCallback((fileId: number): ExtractionRequestBody => {
    const selectedBoard = getSavedBoardSelection();
    const board_id = selectedBoard?.id ? Number(selectedBoard.id) : null;
    const source = localFile?.isCCImage ? "companycam" : "swivu";
    return { file_id: fileId, source, board_id };
  }, [localFile?.isCCImage]);

  // Extraction handlers
  const handleRunExtraction = useCallback((type: ExtractionType) => {
    if (!localFile) return;

    // Fire-and-forget: each type manages its own state slice independently
    const run = async () => {
      setExtractionStates((prev) => ({
        ...prev,
        [type]: { loading: true, loadingPhase: "analyzing", error: null, results: prev[type]?.results ?? null },
      }));

      try {
        await awaitDocumentText();

        setExtractionStates((prev) => ({
          ...prev,
          [type]: { ...prev[type]!, loadingPhase: "extracting" },
        }));

        const results = await runExtraction(type, buildExtractionBody(localFile.id as number));
        if (results) {
          setExtractionStates((prev) => ({
            ...prev,
            [type]: { loading: false, loadingPhase: null, error: null, results },
          }));
        }
      } catch (error: any) {
        const message =
          error?.response?.data?.error ??
          error?.message ??
          "Extraction failed";
        setExtractionStates((prev) => ({
          ...prev,
          [type]: { loading: false, loadingPhase: null, error: typeof message === "string" ? message : String(message), results: null },
        }));
      }
    };

    run();
  }, [localFile, runExtraction, awaitDocumentText, buildExtractionBody]);

  const handleRunAllExtractions = useCallback(() => {
    EXTRACTION_OPERATIONS.forEach((op) => {
      const state = extractionStates[op.type];
      if (!state?.loading && !state?.results) {
        handleRunExtraction(op.type);
      }
    });
  }, [handleRunExtraction, extractionStates]);

  const handleViewExtraction = useCallback((type: ExtractionType) => {
    const state = extractionStates[type];
    if (state?.results || state?.loading) {
      // Already has results or is actively loading — navigate to the results view
      setActiveExtraction(type);
    } else {
      handleRunExtraction(type);
    }
  }, [extractionStates, handleRunExtraction]);

  const handleCloseExtractionPanel = useCallback(() => {
    cancelAllExtractions();
    clearSearch();
    setIsExtractionPanelOpen(false);
    setActiveExtraction(null);
    setExtractionStates({});
  }, [cancelAllExtractions, clearSearch]);

  const handleBackToOperationList = useCallback(() => {
    clearSearch();
    setActiveExtraction(null);
    // Leave extractionStates intact so parallel extractions keep running
  }, [clearSearch]);

  // Reset extraction panel and all states when file changes
  useEffect(() => {
    handleCloseExtractionPanel();
    cancelPrefetch();
  }, [localFile?.id]);

  // Prefetch document text cache when extraction panel opens for extractable files
  useEffect(() => {
    if (isExtractionPanelOpen && localFile?.id && isExtractable) {
      prefetchDocumentText(buildExtractionBody(localFile.id as number));
    }
  }, [isExtractionPanelOpen, localFile?.id, isExtractable]);

  // Reset tab when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab("review");
    }
  }, [open]);

  // Add keyboard listener for navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        // Don't handle navigation if user is typing in an input field
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (
          activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.hasAttribute("contenteditable")
        );

        if (isInputFocused) {
          return; // Let the input handle the arrow keys normally
        }

        e.preventDefault();
        if (e.key === "ArrowLeft") {
          handlePrevious();
        } else {
          handleNext();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [open, handlePrevious, handleNext]);

  const value: FileDetailModalContextValue = {
    // File state
    file: localFile,
    files,
    imageFile,
    imageUrlToLoad,

    // Image loading state
    imageLoading,
    showSpinner,
    displayedImageUrl,
    currentImageUrl,
    handleLoadComplete,

    // Navigation state
    hasPrevious,
    hasNext,
    handlePrevious,
    handleNext,

    // File permissions
    canEditFile,
    handleFileDownload,
    deleteFile,

    // Spot incentive state
    shouldShowAdjustPoints,
    handleAdjustPoints,

    // UI state
    activeTab,
    setActiveTab,
    summaryTab,
    setSummaryTab,
    isAnnotating,
    setIsAnnotating,
    isFullscreen,
    toggleFullscreen,
    isAnalyzeDialogOpen,
    setIsAnalyzeDialogOpen,

    // Modal state
    open,
    setOpen,

    // Computed values
    metadataRows,
    shouldShowSpotIncentiveButton,

    // External dependencies
    entityRecord,
    project,
    swivuAppGrants,
    editorHandler,
    handleFileOpen,

    // Handlers
    handleAnnotating,
    handleCreateSpotIncentive,

    fileChangesVersion,

    // Extraction panel state
    isExtractable,
    isExtractionPanelOpen,
    setIsExtractionPanelOpen,
    activeExtraction,
    extractionStates,
    handleRunExtraction,
    handleRunAllExtractions,
    handleViewExtraction,
    handleCloseExtractionPanel,
    handleBackToOperationList,

    // PDF search
    pdfSearchText,
    handleSearchInPdf,
    clearPdfSearch: clearSearch,

    // Entity link
    copyEntityLink,

    // Promote panel state
    isPromotePanelOpen,
    setIsPromotePanelOpen,
    isFilePromoted,
    handleClosePromotePanel,
    promoteAiSuggestions,
    setPromoteAiSuggestions,
    promoteVisibilityWarnings,
    setPromoteVisibilityWarnings,
  };

  return <FileDetailModalContext.Provider value={value}>{children}</FileDetailModalContext.Provider>;
};

export const useFileDetailModal = () => {
  const context = useContext(FileDetailModalContext);
  if (context === undefined) {
    throw new Error("useFileDetailModal must be used within a FileDetailModalProvider");
  }
  return context;
};
